/* Keep the initial hero first in the network queue; prepare images before interaction. */
(() => {
  if (window.HomepageMedia) return;
  const heroes = new WeakMap();
  const strips = new WeakMap();
  function loadHeroImage(image, priority) {
    if (priority) image.fetchPriority = priority;
    const picture = image.closest('picture');
    picture.querySelectorAll('source[data-hero-srcset]').forEach(source => {
      source.srcset = source.dataset.heroSrcset;
      source.removeAttribute('data-hero-srcset');
    });
    if (image.dataset.heroSrc) {
      image.src = image.dataset.heroSrc;
      image.removeAttribute('data-hero-src');
    }
  }
  function imageReady(image) {
    return new Promise(resolve => {
      const done = () => {
        image.removeEventListener('load', done);
        image.removeEventListener('error', done);
        resolve();
      };
      if (image.complete && image.getAttribute('src')) done();
      else {
        image.addEventListener('load', done, { once: true });
        image.addEventListener('error', done, { once: true });
      }
    });
  }
  function prepareHero(hero) {
    if (heroes.has(hero)) return heroes.get(hero);
    const images = Array.from(hero.querySelectorAll('picture > img'));
    if (!images.length) return Promise.resolve();
    const warm = priority => images.slice(1).forEach(image => loadHeroImage(image, priority));
    // Intent overrides background priority, including a fast swipe or pagination click.
    hero.addEventListener('pointerdown', () => warm('high'), { passive: true });
    hero.addEventListener('pointerenter', () => warm('low'), { passive: true, once: true });
    hero.addEventListener('focusin', () => warm('high'));
    const ready = imageReady(images[0]).then(() => {
      warm('low');
      return Promise.all(images.map(imageReady));
    });
    heroes.set(hero, ready);
    return ready;
  }
  function attachHero(hero, swiper) {
    const ready = prepareHero(hero);
    // Start the existing four-second rotation after images are ready, never on a blank slide.
    swiper.autoplay.stop();
    ready.then(() => {
      if (!swiper.destroyed) swiper.autoplay.start();
    });
  }
  function prepareCircles(strip) {
    if (strips.has(strip)) return;
    const images = Array.from(strip.querySelectorAll('[data-circle-src]'));
    const load = image => {
      if (!image.dataset.circleSrc) return;
      image.src = image.dataset.circleSrc;
      image.removeAttribute('data-circle-src');
    };
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) { load(entry.target); observer.unobserve(entry.target); }
      });
    }, { root: strip, rootMargin: '0px 400px', threshold: 0 }) : null;
    images.forEach(image => observer ? observer.observe(image) : load(image));
    // A gesture can travel beyond the look-ahead region; warm the whole small strip on intent.
    const warm = () => { images.forEach(load); observer?.disconnect(); };
    strip.addEventListener('pointerdown', warm, { passive: true, once: true });
    strip.addEventListener('focusin', warm, { once: true });
    strips.set(strip, observer);
  }
  function init(root = document) {
    const find = selector => [ ...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector) ];
    find('.home-slideshow').forEach(prepareHero);
    find('[data-home-circle-strip]').forEach(prepareCircles);
  }
  window.HomepageMedia = { init, attachHero };
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-home-circle-strip]').forEach(strip => strips.get(strip)?.disconnect());
  });
})();
